<template>
  <div>
    <div v-if="!linking">
      <button class="btn btn-primary" :disabled="loadingASPSPs" @click="linkAccounts()">
        <span
          v-if="loadingASPSPs"
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        {{ !app.active ? 'Activate by linking accounts' : 'Link accounts' }}
      </button>
      <button class="btn btn-outline-primary ml-2" v-if="app.pendingActionType != 'activation'" @click="showModal">
          Request {{ !app.active ? 'activation' : 'unrestriction' }}
      </button>
      <b-modal ref="appModalRef" hide-header hide-footer centered lazy title="Application Activation or Unrestriction">
        <template v-if="billingAccounts === null">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </template>
        <template v-else-if="hasBillingAccounts">
          <p>Are you sure you want to {{ !app.active ? 'activate' : 'unrestrict' }} the application "{{ app.name }}" ({{ appKid }})?</p>
          <p class="small">Please select the billing account to be used for the application:</p>
          <select class="form-control mb-3" v-model="selectedBillingAccountId">
            <option v-for="billingAccount in billingAccounts" :value="billingAccount.id" :key="billingAccount.id">{{ billingAccount.company_name }}</option>
          </select>
          <p class="small">The application will be reviewed by our personnel</p>
          <button class="btn btn-primary" @click="activate">
            <span v-if="activating" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            Request
          </button>
        </template>
        <template v-else>
          <p>The application "{{ app.name }}" ({{ appKid }}) can be {{ !app.active ? 'activated' : 'unrestricted' }} only after you have a billing account with Enable Banking.</p>
          <p class="small">
            Please note that unrestricted access in the production environment is available only to organisations. Individuals can activate applications by linking their accounts and may continue using them in restricted mode, adding more linked accounts if needed.
          </p>
          <p class="small">
            To use the Enable Banking API without restrictions, please request a tailored pricing quote and sign a contract. If your company has already signed a contract, please contact our support team to associate your user profile with the company's billing account.
          </p>
          <button class="btn btn-primary" @click="$router.push('/cp/billing')">
            Get a quote
          </button>
        </template>
        <button class="btn ml-2" @click="hideModal">
          Cancel
        </button>
      </b-modal>
    </div>
    <div v-else>
      <div class="form-row align-items-center">
        <div class="col">
          <select
            class="form-control"
            required
            :disabled="linkingStated"
            v-model="country"
            v-on:change="aspsp = ''; usage = ''"
          >
            <option value disabled selected>Country</option>
            <option
              v-for="country in Object.keys(countries).sort((a, b) => countriesList[a].localeCompare(countriesList[b]))"
              :value="country"
              :key="`${appKid}-country-${country}`"
            >{{ countriesList[country] }}</option>
          </select>
        </div>
        <div class="col">
          <select
            class="form-control"
            required
            :disabled="!country || linkingStated"
            v-model="aspsp"
            v-on:change="usage = ''"
          >
            <option value disabled selected>ASPSP</option>
            <option
              v-for="aspsp in countries[country]"
              :value="aspsp"
              :key="`${appKid}-aspsp-${country}-${aspsp.name.replace(/\s+/g, '-')}`"
            >{{ aspsp.name }}</option>
          </select>
        </div>
        <div class="col">
          <select
            class="form-control"
            required
            :disabled="!aspsp || linkingStated"
            v-model="usage"
          >
            <option value disabled selected>Usage type</option>
            <option
              v-for="usage in aspsp ? aspsp.psu_types : []"
              :value="usage"
              :key="`${appKid}-aspsp-${country}-${aspsp.name.replace(/\s+/g, '-')}-${usage}`"
            >{{ usage }}</option>
          </select>
        </div>
        <div class="col">
          <button
            class="btn btn-primary"
            :disabled="!usage || linkingStated"
            @click="startAccountsLinking()"
          >
            <span
              v-if="linkingStated"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            Link
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios'
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'AccountsLinking',
  data() {
    return {
      loadingASPSPs: false, // when fetching ASPSPs list
      linking: false, // when fetched ASPSPs list and ready to link
      linkingStated: false, // when linking started
      activating: false,
      countries: null,
      country: '',
      aspsp: '',
      usage: '',
      selectedBillingAccountId: null
    }
  },
  props: {
    app: {
      type: Object
    }
  },
  computed: {
    ...mapGetters(['user', 'idToken', 'billingAccounts', 'billingAccountsLoading']),
    ...mapState({
      countriesList: state => state.countriesList
    }),
    appKid() {
      return this.app.kid
    },
    hasBillingAccounts() {
      return !!(this.billingAccounts && this.billingAccounts.length > 0)
    }
  },
  methods: {
    ...mapActions(['pushAlertMessage']),
    showModal() {
      if (this.hasBillingAccounts) {
        this.selectedBillingAccountId = this.billingAccounts[0].id // set the first billing account as the default
      }
      this.$refs.appModalRef.show()
    },
    hideModal() {
      this.$refs.appModalRef.hide()
    },
    async linkAccounts() {
      this.loadingASPSPs = true
      try {
        const aspspsResponse = await axios.get(
          '/api/aspsps',
          { headers: { Authorization: `Bearer ${this.idToken.token}` } }
        )
        const countries = {}
        for (const aspsp of aspspsResponse.data.aspsps) {
          const countryASPSPs = countries[aspsp.country] || []
          aspsp.psu_types = aspsp.psu_types.sort()
          countryASPSPs.push(aspsp)
          countries[aspsp.country] = countryASPSPs.sort((a, b) => a.name.localeCompare(b.name))
        }
        this.countries = countries
        this.linking = true
      } catch (e) {
        this.pushAlertMessage({ message: 'Unable to get ASPSPs' })
      } finally {
        this.loadingASPSPs = false
      }
    },
    async startAccountsLinking() {
      this.linkingStated = true
      const query = new URLSearchParams()
      query.set('country', this.country)
      query.set('aspsp', this.aspsp.name)
      query.set('appId', this.appKid)
      query.set('psuType', this.usage)
      query.set('redirectUrl', `${window.location.origin}/api/auth_redirect`)
      try {
        const linkAccountsResponse = await axios.post(
          '/api/link_accounts',
          query.toString(),
          { headers: { Authorization: `Bearer ${this.idToken.token}` } }
        )
        window.location.href = linkAccountsResponse.data.url
      } catch (e) {
        this.pushAlertMessage({ message: 'Unable to start accounts linking' })
        this.linkingStated = false
      }
    },
    async activate() {
      this.activating = true
      try {
        const payload = { appId: this.appKid, billingAccountId: this.selectedBillingAccountId }
        await axios.put('/api/applications', payload, { headers: { Authorization: `Bearer ${this.idToken.token}` } }) // activate endpoint
        this.pushAlertMessage({ message: 'Your activation request is pending, please wait for the approval.', type: 'info' })
      } catch (e) {
        this.pushAlertMessage({ message: 'Unable to activate application' })
      } finally {
        this.hideModal()
        await this.updateApplication(this.appKid)
        this.activating = false
      }
    }
  }
}
</script>
